import { SsoConnectionUser } from '@choco-admin/types';
import { formatDateWithPreset } from '@choco-admin/utils';
import type { SortDescriptor } from '@heroui/react';
import {
  Button,
  Checkbox,
  Chip,
  Divider,
  Select,
  SelectItem,
  TableCell,
  TableRow,
} from '@heroui/react';
import React, { type Key, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft, FiUsers } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageHeader } from '../../../../components/PageHeader';
import {
  type LoadingTableColumn,
  LinkCell,
  TableHeader,
  LoadingTable,
} from '../../../../components/Table';
import { useSsoConnectionUsers, useSsoConnections } from '../../hooks';
import { mappedValue, userConfigIssues } from '../../utils';

import { ConnectionSummary } from './ConnectionSummary';
import { ExportUsersButton } from './ExportUsersButton';
import { ColumnLabel } from './InfoTooltip';
import { RoleMappingsSection } from './RoleMappingsSection';
import { UserDetailDrawer } from './UserDetailDrawer';
import { UserIssueWarning } from './UserIssueWarning';

const USER_ROLES_EXPLANATION = [
  'The roles this provider sent for this person the last time they signed in. They are the provider\u2019s own wording.',
  'A role only gives access if it is listed under Role mappings above. Anything else is sent but ignored.',
];

const columns: LoadingTableColumn[] = [
  {
    label: 'Email',
    key: 'email',
    allowsSorting: true,
    width: '28%',
    className: 'pl-6',
  },
  {
    label: 'First name',
    key: 'givenName',
    allowsSorting: true,
    width: '10%',
    className: 'hidden md:table-cell',
  },
  {
    label: 'Last name',
    key: 'familyName',
    allowsSorting: true,
    width: '10%',
    className: 'hidden md:table-cell',
  },
  {
    label: 'Roles',
    width: '22%',
    className: 'hidden lg:table-cell',
    headerRender: () => (
      <ColumnLabel label="Roles" paragraphs={USER_ROLES_EXPLANATION} />
    ),
  },
  { label: 'Choco user', width: '6%', className: 'hidden md:table-cell' },
  {
    label: 'Created',
    key: 'createdAt',
    allowsSorting: true,
    width: '12%',
    className: 'hidden lg:table-cell whitespace-nowrap',
  },
  {
    label: 'Modified',
    key: 'lastModifiedAt',
    allowsSorting: true,
    width: '12%',
    className: 'hidden lg:table-cell whitespace-nowrap pr-6',
  },
];

type SortableUserColumn =
  | 'email'
  | 'givenName'
  | 'familyName'
  | 'createdAt'
  | 'lastModifiedAt';

const SORTABLE_COLUMNS: SortableUserColumn[] = [
  'email',
  'givenName',
  'familyName',
  'createdAt',
  'lastModifiedAt',
];

const toSortableColumn = (column?: Key): SortableUserColumn =>
  SORTABLE_COLUMNS.find((candidate) => candidate === column) ?? 'email';

const sortValue = (user: SsoConnectionUser, column: SortableUserColumn) => {
  if (column === 'email') {
    return (user.email ?? '').toLowerCase();
  }
  if (column === 'givenName') {
    return (mappedValue(user, 'given_name') ?? '').toLowerCase();
  }
  if (column === 'familyName') {
    return (mappedValue(user, 'family_name') ?? '').toLowerCase();
  }
  // Dates are ISO 8601, so a lexical compare matches chronological order and
  // keeps missing values sorting together at one end.
  return user[column] ?? '';
};

const formatDate = (value?: string): string =>
  value ? formatDateWithPreset(value, 'DAY_FIRST_MEDIUM') : '—';

const BackToConnections: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Button
      variant="flat"
      color="default"
      startContent={<FiArrowLeft className="h-4 w-4" />}
      onPress={() => navigate('/sso')}
    >
      Back to SSO connections
    </Button>
  );
};

export const ConnectionDetail: React.FC = () => {
  const { connectionId = '' } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'email',
    direction: 'ascending',
  });
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [openUser, setOpenUser] = useState<SsoConnectionUser | null>(null);

  const { connections } = useSsoConnections();
  const connection = connections.find((item) => item.id === connectionId);

  const { users, tooLarge, notFound, isLoading, error, refetch } =
    useSsoConnectionUsers({ connectionId });

  // Options come from the roles actually present, so the filter can never offer
  // a role that would return nothing.
  const availableRoles = useMemo(
    () =>
      Array.from(new Set(users.flatMap((user) => user.federatedRoles))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [users]
  );

  const issuesByUser = useMemo(
    () => new Map(users.map((user) => [user.id, userConfigIssues(user)])),
    [users]
  );

  const usersNeedingAttention = useMemo(
    () =>
      users.filter((user) => (issuesByUser.get(user.id) ?? []).length > 0)
        .length,
    [users, issuesByUser]
  );

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return users.filter((user) => {
      // Ignored once nothing is wrong, so a checkbox left ticked cannot empty
      // the table after the last error is fixed.
      if (onlyErrors && usersNeedingAttention > 0) {
        if ((issuesByUser.get(user.id) ?? []).length === 0) {
          return false;
        }
      }

      const matchesRole =
        selectedRoles.size === 0 ||
        user.federatedRoles.some((role) => selectedRoles.has(role));

      if (!matchesRole) {
        return false;
      }

      if (!term) {
        return true;
      }

      return [
        user.email,
        user.chocoUserId,
        ...user.federatedRoles,
        ...user.mappedAttributes.map((attribute) => attribute.value),
      ]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(term));
    });
  }, [
    users,
    searchTerm,
    selectedRoles,
    onlyErrors,
    usersNeedingAttention,
    issuesByUser,
  ]);

  const visibleUsers = useMemo(() => {
    const column = toSortableColumn(sortDescriptor.column);
    const factor = sortDescriptor.direction === 'descending' ? -1 : 1;

    return [...filteredUsers].sort(
      (a, b) =>
        sortValue(a, column).localeCompare(sortValue(b, column)) * factor
    );
  }, [filteredUsers, sortDescriptor]);

  if (notFound) {
    return (
      <div className="p-6">
        <ErrorCard
          title="SSO connection not found"
          message={`No identity provider matches "${connectionId}".`}
          backLinkTo="/sso"
          backLinkText="Back to SSO connections"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorCard
          title="Could not load SSO users"
          message={error.message}
          onRetry={refetch}
          backLinkTo="/sso"
          backLinkText="Back to SSO connections"
        />
      </div>
    );
  }

  if (tooLarge) {
    return (
      <div className="space-y-6">
        <BackToConnections />
        <PageHeader title={connection?.name ?? 'SSO connection'} />
        {connection && <ConnectionSummary connection={connection} />}
        <RoleMappingsSection
          connectionId={connectionId}
          connectionName={connection?.name ?? 'this connection'}
          users={[]}
          isUserCountKnown={false}
        />
        <ErrorCard
          title="Too many users to list"
          message={tooLarge.message}
          backLinkTo="/sso"
          backLinkText="Back to SSO connections"
        />
      </div>
    );
  }

  const renderRow = (user: SsoConnectionUser) => (
    <TableRow
      key={user.id}
      className="cursor-pointer hover:bg-content2"
      onClick={() => setOpenUser(user)}
    >
      <TableCell className="pl-6 font-medium text-foreground">
        <span className="flex flex-wrap items-center gap-2 break-words">
          <UserIssueWarning user={user} />
          {user.email ?? '—'}
          {!user.enabled && (
            <Chip size="sm" variant="flat" color="warning">
              Disabled
            </Chip>
          )}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell text-default-500">
        {mappedValue(user, 'given_name') ?? '—'}
      </TableCell>
      <TableCell className="hidden md:table-cell text-default-500">
        {mappedValue(user, 'family_name') ?? '—'}
      </TableCell>
      <TableCell className="hidden lg:table-cell break-words text-default-500">
        {user.federatedRoles.length > 0 ? user.federatedRoles.join(', ') : '—'}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {user.chocoUserId ? (
          <LinkCell
            to={`/users/${user.chocoUserId}`}
            newWindow
            onClick={(event) => event.stopPropagation()}
          >
            Open
          </LinkCell>
        ) : (
          <span className="text-default-500">Not linked</span>
        )}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-default-500">
        {formatDate(user.createdAt)}
      </TableCell>
      <TableCell className="hidden lg:table-cell pr-6 text-default-500">
        {formatDate(user.lastModifiedAt)}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      <Helmet>
        <title>
          {connection?.name
            ? `${connection.name} - Choco Admin`
            : 'SSO Connection - Choco Admin'}
        </title>
      </Helmet>
      <BackToConnections />
      <PageHeader
        title={connection?.name ?? 'SSO connection'}
        description="SSO connection details and the people who sign in through it."
      />

      {connection && <ConnectionSummary connection={connection} />}

      <RoleMappingsSection
        connectionId={connectionId}
        connectionName={connection?.name ?? 'this connection'}
        users={users}
        isUserCountKnown={!isLoading}
      />

      <div className="pt-2">
        <Divider className="mb-6" />
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Users
          {!isLoading && (
            <span className="ml-2 text-sm font-normal text-default-500">
              {visibleUsers.length} of {users.length}
            </span>
          )}
          {!isLoading && usersNeedingAttention > 0 && (
            <span className="ml-2 text-sm font-normal text-warning">
              {usersNeedingAttention} need attention
            </span>
          )}
        </h2>
      </div>

      <TableHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search users by email, name or role"
        searchClassName="sm:w-[320px] sm:flex-none"
        trailing={
          <ExportUsersButton
            users={users}
            connectionName={connection?.name ?? connectionId}
            isDisabled={isLoading || users.length === 0}
          />
        }
        isLoading={isLoading}
      >
        {usersNeedingAttention > 0 && (
          // HeroUI's Checkbox directly rather than ChocoCheckbox: that one omits
          // `classNames`, and its own gap plus HeroUI's default `me-2` on the
          // wrapper leaves the label a long way from the box. The square corners
          // and border are kept, so it still reads as the same control.
          <Checkbox
            size="sm"
            className="shrink-0"
            classNames={{
              base: 'inline-flex gap-1.5 m-0',
              label: 'whitespace-nowrap text-sm text-foreground',
              wrapper:
                'me-0 rounded-md before:rounded-md before:border-default-400 after:rounded-md',
            }}
            isSelected={onlyErrors}
            onValueChange={setOnlyErrors}
          >
            Only users with errors
          </Checkbox>
        )}

        {availableRoles.length > 0 && (
          <Select
            aria-label="Filter by role"
            placeholder="All roles"
            selectionMode="multiple"
            size="sm"
            variant="bordered"
            className="w-full shrink-0 grow-0 sm:w-[200px]"
            selectedKeys={selectedRoles}
            onSelectionChange={(keys) =>
              setSelectedRoles(new Set(Array.from(keys, String)))
            }
          >
            {availableRoles.map((role) => (
              <SelectItem key={role}>{role}</SelectItem>
            ))}
          </Select>
        )}
      </TableHeader>

      <LoadingTable<SsoConnectionUser>
        columns={columns}
        ariaLabel="SSO connection users"
        isLoading={isLoading}
        isEmpty={!isLoading && visibleUsers.length === 0}
        emptyState={{
          icon: FiUsers,
          title: searchTerm ? 'No matching users' : 'No users',
          description: searchTerm
            ? 'No user on this connection matches your search.'
            : 'Nobody has signed in through this connection yet.',
        }}
        data={visibleUsers}
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        renderRow={renderRow}
      />

      <UserDetailDrawer user={openUser} onClose={() => setOpenUser(null)} />
    </div>
  );
};
